import { Link } from "react-router-dom";
import { ArrowLeft, Linkedin, Facebook, Instagram, Globe } from "lucide-react";

/**
 * Shared shell for the /terms, /privacy and /refund pages. Keeps typography
 * consistent with the funnel while making the copy scannable and readable.
 */
export default function LegalLayout({ title, effective, children, testId }) {
  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white grain overflow-x-hidden">
      {/* Top nav */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-bold uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
            Back to Protego
          </Link>
          <Link to="/" className="text-lg font-black tracking-tighter">
            Protego <span className="text-[#F04632]">AI</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main data-testid={testId} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
          Protego AI Inc. · section3.ai
        </p>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[0.95] mb-4">
          {title}
        </h1>
        {effective && (
          <p className="text-sm text-white/50 mb-12 font-mono">Effective: {effective}</p>
        )}

        <article className="legal-prose text-white/75 text-base sm:text-lg leading-relaxed space-y-6">
          {children}
        </article>

        <div className="mt-16 pt-8 border-t border-white/10 text-sm text-white/50">
          <p>
            Questions? Email{" "}
            <a
              href="mailto:hello@protegoos.com"
              className="text-white font-semibold hover:text-[#F04632] transition-colors"
            >
              hello@protegoos.com
            </a>
          </p>
        </div>
      </main>

      {/* Slim footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-white/40">
          <p>
            © 2026 Protego AI Inc. ·{" "}
            <a
              href="https://section3.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              section3.ai
            </a>
          </p>
          <div className="flex items-center gap-4 uppercase tracking-widest font-bold">
            <Link to="/terms" className="hover:text-white transition-colors">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy
            </Link>
            <Link to="/refund" className="hover:text-white transition-colors">
              Refund
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://section3.ai"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Website"
              className="text-white/50 hover:text-white transition-colors"
            >
              <Globe className="w-4 h-4" />
            </a>
            <a
              href="https://linkedin.com/company/protego-ai/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="text-white/50 hover:text-white transition-colors"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a
              href="https://www.facebook.com/protegoos"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="text-white/50 hover:text-white transition-colors"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://www.instagram.com/protegoos"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-white/50 hover:text-white transition-colors"
            >
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
